import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS, IMAGES } from "../data/site";
import { useSite } from "../context/SiteContext";

export default function Navbar() {
  const { settings } = useSite();
  const logo = (settings.images && settings.images.logo) || IMAGES.logo;
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || pathname.startsWith("/card");
  const dark = !solid;
  const linkCls = dark ? "text-bone/80 hover:text-bone" : "text-forest/70 hover:text-forest";
  const testId = (label) => label.toLowerCase().replace(/\s+/g, "-");

  return (
    <header
      data-testid="main-navbar"
      className={`font-menu fixed top-9 inset-x-0 z-50 transition-colors duration-500 ${
        solid ? "bg-bone/90 backdrop-blur-xl border-b border-line" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className="w-11 h-11 rounded-full bg-white shadow-sm flex items-center justify-center overflow-hidden shrink-0">
            <img src={logo} alt="KCC logo" className="w-full h-full object-contain" />
          </span>
          <span className={`font-serif text-lg md:text-xl tracking-tight leading-none ${dark ? "text-bone" : "text-forest"}`}>
            Kanggui <span className="italic text-gold">Christian</span> College
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => {
            const active = l.to === "/" ? pathname === "/" : pathname.startsWith(l.to);
            if (l.children) {
              return (
                <div key={l.to} className="relative group">
                  <Link to={l.to} data-testid={`nav-link-${testId(l.label)}`} className={`relative inline-flex items-center gap-1 text-sm uppercase tracking-widest transition-colors ${linkCls}`}>
                    {l.label}
                    <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                    {active && <motion.span layoutId="nav-underline" className="absolute -bottom-2 left-0 right-0 h-px bg-gold" />}
                  </Link>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                    <div className="bg-bone border border-line rounded-md shadow-xl py-2 min-w-[190px]">
                      {l.children.map((ch) => (
                        ch.external ? (
                          <a key={ch.href} href={ch.href} target="_blank" rel="noopener noreferrer" data-testid={`nav-sublink-${testId(ch.label)}`} className="flex items-center px-5 py-2.5 text-sm uppercase tracking-widest text-forest/75 hover:text-forest hover:bg-line/40 transition-colors">
                            <span className="text-gold mr-2">{">>"}</span>{ch.label}
                          </a>
                        ) : (
                          <Link key={ch.to} to={ch.to} data-testid={`nav-sublink-${testId(ch.label)}`} className="flex items-center px-5 py-2.5 text-sm uppercase tracking-widest text-forest/75 hover:text-forest hover:bg-line/40 transition-colors">
                            <span className="text-gold mr-2">{">>"}</span>{ch.label}
                          </Link>
                        )
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <Link key={l.to} to={l.to} data-testid={`nav-link-${testId(l.label)}`} className={`relative text-sm uppercase tracking-widest transition-colors ${linkCls}`}>
                {l.label}
                {active && <motion.span layoutId="nav-underline" className="absolute -bottom-2 left-0 right-0 h-px bg-gold" />}
              </Link>
            );
          })}
          <Link
            to="/login"
            data-testid="nav-admin-btn"
            className={`text-xs uppercase tracking-widest px-5 py-2.5 rounded-full transition-transform hover:-translate-y-0.5 ${
              dark ? "bg-bone text-forest" : "bg-forest text-bone"
            }`}
          >
            Admin
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`lg:hidden ${dark ? "text-bone" : "text-forest"}`}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden overflow-hidden bg-forest text-bone"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <div key={l.to}>
                  <Link to={l.to} data-testid={`mobile-nav-${testId(l.label)}`} className="font-serif text-3xl">
                    {l.label}
                  </Link>
                  {l.children && (
                    <div className="mt-3 ml-4 flex flex-col gap-3 border-l border-bone/20 pl-4">
                      {l.children.map((ch) => (
                        ch.external ? (
                          <a key={ch.href} href={ch.href} target="_blank" rel="noopener noreferrer" data-testid={`mobile-nav-${testId(ch.label)}`} className="text-sm uppercase tracking-widest text-bone/70">
                            {ch.label}
                          </a>
                        ) : (
                          <Link key={ch.to} to={ch.to} data-testid={`mobile-nav-${testId(ch.label)}`} className="text-sm uppercase tracking-widest text-bone/70">
                            {ch.label}
                          </Link>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Link to="/login" data-testid="mobile-nav-admin" className="mt-2 inline-block text-xs uppercase tracking-widest text-gold">
                Admin Login →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
